import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./master/SideBar";
import Nav from "./master/NavBar";
import { getSidebarManagements } from "./master/scaiManagementConfig";

// Navigation metadata is sourced ONLY from scaiManagementConfig.js.
// No access filtering here while the management catalog is being finalized.
const FAVORITES_KEY = "scai-launchpad-favorites-v1";
const RECENT_KEY = "scai-launchpad-recents-v1";

function readSavedList(key) {
  try {
    const saved = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(saved) ? saved.filter((value) => typeof value === "string") : [];
  } catch {
    return [];
  }
}

function AppTile({ item, index, favorite, onFavorite, onOpen }) {
  const label = item.label || item.title || item.key;
  const path = item.path || item.defaultPath || "/";
  const isExternal = item.external && /^https?:\/\//i.test(path);
  const content = (
    <>
      <span className={`scai-launch-icon scai-launch-color-${index % 8}`}>
        <i className={item.icon || "ri-apps-2-line"} aria-hidden="true" />
      </span>
      <span className="scai-launch-label" title={label}>{label}</span>
    </>
  );

  return (
    <div className="scai-launch-tile">
      {isExternal ? (
        <a href={path} target="_blank" rel="noopener noreferrer" className="scai-launch-link" onClick={onOpen}>
          {content}
        </a>
      ) : (
        <Link to={path} className="scai-launch-link" onClick={onOpen}>{content}</Link>
      )}
      <button
        type="button"
        className={`scai-launch-star ${favorite ? "selected" : ""}`}
        onClick={() => onFavorite(item.key)}
        title={favorite ? `Remove ${label} from favorites` : `Add ${label} to favorites`}
        aria-label={favorite ? `Remove ${label} from favorites` : `Add ${label} to favorites`}
        aria-pressed={favorite}
      >
        <i className={favorite ? "ri-star-fill" : "ri-star-line"} />
      </button>
    </div>
  );
}

export default function Index4() {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("all");
  const [sort, setSort] = useState("default");
  const [favorites, setFavorites] = useState(() => readSavedList(FAVORITES_KEY));
  const [recent, setRecent] = useState(() => readSavedList(RECENT_KEY));

  const managements = useMemo(() => getSidebarManagements(), []);
  const categories = useMemo(
    () => [...new Set(managements.map((item) => item.category || item.group).filter(Boolean))].sort(),
    [managements]
  );

  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    const matches = managements.filter((item) => {
      if (view === "favorites" && !favorites.includes(item.key)) return false;
      if (view === "recent" && !recent.includes(item.key)) return false;
      if (view.startsWith("category:") && (item.category || item.group) !== view.slice(9)) return false;
      if (!query) return true;
      return [item.label, item.title, item.subtitle, item.key, ...(item.tabs || []).map((tab) => tab.title || tab.label)]
        .some((text) => String(text || "").toLocaleLowerCase().includes(query));
    });
    if (view === "favorites") matches.sort((a, b) => favorites.indexOf(a.key) - favorites.indexOf(b.key));
    else if (view === "recent") matches.sort((a, b) => recent.indexOf(a.key) - recent.indexOf(b.key));
    else if (sort === "alphabetical") matches.sort((a, b) =>
      (a.label || a.title || "").localeCompare(b.label || b.title || "")
    );
    return matches;
  }, [managements, search, view, sort, favorites, recent]);

  useEffect(() => {
    const handleKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
      if (event.key === "Escape" && document.activeElement === searchRef.current) {
        setSearch("");
        searchRef.current.blur();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const toggleFavorite = (key) => {
    setFavorites((previous) => {
      const next = previous.includes(key) ? previous.filter((item) => item !== key) : [...previous, key];
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
      return next;
    });
  };

  const trackOpen = (key) => {
    setRecent((previous) => {
      const next = [key, ...previous.filter((item) => item !== key)].slice(0, 12);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  };

  const openFirst = () => {
    const first = filtered[0];
    if (!first) return;
    const path = first.path || first.defaultPath || "/";
    trackOpen(first.key);
    if (first.external && /^https?:\/\//i.test(path)) window.open(path, "_blank", "noopener,noreferrer");
    else navigate(path);
  };

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Header />
        <div className="layout-page">
          <Nav />
          <main className="content-wrapper scai-launchpad">
            <div className="container-fluid scai-launch-body">
              {/* {localStorage.getItem('user_permissions')} */}
              {/* {localStorage.getItem('staff_access') } */}
              <header className="scai-launch-header p-2">
                <div className="scai-launch-heading">
                  <span className="scai-launch-heading-icon"><i className="ri-apps-2-line" /></span>
                  <div>
                    <h1>Applications</h1>
                    <p>SCAI School ERP <span aria-hidden="true">·</span> {managements.length} management modules</p>
                  </div>
                </div>
                <div className="scai-launch-tools">
                  <label className="scai-launch-search">
                    <i className="ri-search-line" aria-hidden="true" />
                    <input
                      ref={searchRef}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      onKeyDown={(event) => { if (event.key === "Enter") openFirst(); }}
                      placeholder="Search applications or tabs..."
                      aria-label="Search applications"
                    />
                    {search ? (
                      <button type="button" onClick={() => { setSearch(""); searchRef.current?.focus(); }} aria-label="Clear search">
                        <i className="ri-close-line" />
                      </button>
                    ) : <kbd>⌘ K</kbd>}
                  </label>
                  <label className="scai-launch-sort" title="Sort applications">
                    <i className="ri-sort-asc" />
                    <select value={sort} onChange={(event) => setSort(event.target.value)} aria-label="Sort applications">
                      <option value="default">Default order</option>
                      <option value="alphabetical">Name A–Z</option>
                    </select>
                  </label>
                </div>
              </header>

              <div className="scai-launch-filters" role="group" aria-label="Application views">
                <button type="button" className={view === "all" ? "active" : ""} onClick={() => setView("all")}>All apps</button>
                <button type="button" className={view === "favorites" ? "active" : ""} onClick={() => setView("favorites")}><i className="ri-star-line" /> Favorites</button>
                <button type="button" className={view === "recent" ? "active" : ""} onClick={() => setView("recent")}><i className="ri-history-line" /> Recent</button>
                {categories.map((name) => (
                  <button key={name} type="button" className={view === `category:${name}` ? "active" : ""} onClick={() => setView(`category:${name}`)}>{name}</button>
                ))}
                <span className="scai-launch-count">{filtered.length} apps</span>
              </div>

              {filtered.length ? (
                <section className="scai-launch-grid" aria-label="School management applications">
                  {filtered.map((item) => (
                    <AppTile
                      key={item.key}
                      item={item}
                      index={managements.findIndex((entry) => entry.key === item.key)}
                      favorite={favorites.includes(item.key)}
                      onFavorite={toggleFavorite}
                      onOpen={() => trackOpen(item.key)}
                    />
                  ))}
                </section>
              ) : (
                <div className="scai-launch-empty">
                  <i className="ri-search-eye-line" />
                  <strong>No applications found</strong>
                  <p>Try a different search or switch to All apps.</p>
                  <button type="button" onClick={() => { setSearch(""); setView("all"); }}>Show all apps</button>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
      <style>{`
        .scai-launchpad{--launch-bg:#f7f8fc;--launch-card:#fff;--launch-text:#25314b;--launch-muted:#7f8aa3;--launch-border:#e5e9f1;--launch-accent:#6f5af0;background:var(--launch-bg);min-height:calc(100dvh - 76px)}
        .scai-launchpad .scai-launch-body{width:100%;max-width:none!important;margin:0!important;padding:14px 18px 28px!important}
        .scai-launch-header{display:flex;align-items:center;justify-content:space-between;gap:18px;padding:2px 0 13px;border-bottom:1px solid var(--launch-border)}
        .scai-launch-heading{display:flex;align-items:center;gap:11px;min-width:0}.scai-launch-heading-icon{width:45px;height:45px;flex:0 0 45px;display:grid;place-items:center;border-radius:13px;background:linear-gradient(145deg,#8776fa,#6353d7);color:white}.scai-launch-heading-icon i{font-size:25px!important;line-height:1}
        .scai-launch-heading h1{font-size:22px!important;font-weight:800;line-height:1.2;margin:0;color:var(--launch-text)}.scai-launch-heading p{margin:3px 0 0;font-size:11px;color:var(--launch-muted)}
        .scai-launch-tools{display:flex;align-items:center;gap:9px;min-width:0;flex:0 1 560px}
        .scai-launch-search{height:41px;display:flex;align-items:center;gap:9px;flex:1;min-width:0;padding:0 11px;border:1px solid var(--launch-border);background:var(--launch-card);border-radius:11px;color:var(--launch-muted)}.scai-launch-search:focus-within{border-color:#9d91ef;box-shadow:0 0 0 3px rgba(111,90,240,.08)}.scai-launch-search>i{font-size:19px!important}.scai-launch-search input{width:100%;min-width:0;border:0!important;outline:0!important;box-shadow:none!important;background:transparent!important;color:var(--launch-text);font-size:12px}.scai-launch-search input::placeholder{color:var(--launch-muted)}.scai-launch-search kbd{font-size:10px;border:1px solid var(--launch-border);border-radius:5px;padding:3px 5px;color:var(--launch-muted);white-space:nowrap}.scai-launch-search button{border:0;background:transparent;color:var(--launch-muted);cursor:pointer}
        .scai-launch-sort{height:41px;display:flex;align-items:center;gap:7px;padding:0 10px;border:1px solid var(--launch-border);background:var(--launch-card);border-radius:11px;color:var(--launch-muted)}.scai-launch-sort select{border:0;background:transparent;color:var(--launch-text);font-size:11px;font-weight:700;outline:0;max-width:120px}
        .scai-launch-filters{display:flex;align-items:center;gap:7px;overflow-x:auto;scrollbar-width:thin;padding:12px 14px 11px;min-width:0}.scai-launch-filters button{white-space:nowrap;border:1px solid var(--launch-border);background:var(--launch-card);color:var(--launch-muted);border-radius:9px;font-size:11px;font-weight:700;padding:7px 12px;cursor:pointer}.scai-launch-filters button.active{background:var(--launch-accent);border-color:var(--launch-accent);color:white}.scai-launch-filters button:hover:not(.active){color:var(--launch-accent);border-color:#c9c2ff}.scai-launch-count{margin-left:auto;white-space:nowrap;font-size:11px;font-weight:700;color:var(--launch-muted)}
        .scai-launch-grid{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:12px;width:100%}
        .scai-launch-tile{position:relative;min-width:0;min-height:148px;border:1px solid transparent;border-radius:15px;transition:background .16s,border-color .16s,transform .16s}.scai-launch-tile:hover,.scai-launch-tile:focus-within{background:var(--launch-card);border-color:var(--launch-border);transform:translateY(-2px)}
        .scai-launch-link{min-height:148px;padding:15px 7px 12px;display:flex;align-items:center;flex-direction:column;text-align:center;gap:11px;color:var(--launch-text)!important;text-decoration:none!important;outline-offset:-2px}
        .scai-launch-icon{display:grid;place-items:center;width:83px;height:83px;flex:0 0 83px;border-radius:21px;box-shadow:0 9px 20px rgba(38,44,85,.12),inset 0 1px 1px rgba(255,255,255,.2);color:#fff!important}
        .scai-launch-icon i{display:block;font-size:47px!important;line-height:1!important;font-weight:normal!important;color:#fff!important}.scai-launch-label{font-size:12.5px;font-weight:750;line-height:1.4;max-width:100%;overflow-wrap:break-word;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
        .scai-launch-star{position:absolute;top:5px;right:5px;width:27px;height:27px;border:0;border-radius:9px;background:var(--launch-card);color:#a9afc1;opacity:0;cursor:pointer}.scai-launch-tile:hover .scai-launch-star,.scai-launch-star:focus-visible,.scai-launch-star.selected{opacity:1}.scai-launch-star.selected{color:#f0a827}.scai-launch-star i{font-size:16px!important}
        .scai-launch-color-0{background:linear-gradient(140deg,#8978fd,#5d50d8)}.scai-launch-color-1{background:linear-gradient(140deg,#39bce2,#147bd0)}.scai-launch-color-2{background:linear-gradient(140deg,#ffad63,#ef7048)}.scai-launch-color-3{background:linear-gradient(140deg,#47cba9,#189982)}.scai-launch-color-4{background:linear-gradient(140deg,#ee88b8,#ca5293)}.scai-launch-color-5{background:linear-gradient(140deg,#7296ef,#4e6dcc)}.scai-launch-color-6{background:linear-gradient(140deg,#a781e7,#8558be)}.scai-launch-color-7{background:linear-gradient(140deg,#61c1c7,#3b899f)}
        .scai-launch-empty{min-height:230px;display:flex;align-items:center;justify-content:center;flex-direction:column;color:var(--launch-muted);text-align:center;gap:8px}.scai-launch-empty>i{font-size:36px!important;color:var(--launch-accent)}.scai-launch-empty strong{font-size:15px;color:var(--launch-text)}.scai-launch-empty p{font-size:12px;margin:0}.scai-launch-empty button{border:0;border-radius:9px;background:var(--launch-accent);color:#fff;padding:9px 14px;margin-top:4px}
        [data-style="dark"] .scai-launchpad,[data-bs-theme="dark"] .scai-launchpad{--launch-bg:#222335;--launch-card:#2b2c40;--launch-text:#ebebf5;--launch-muted:#abb0c9;--launch-border:#474961;--launch-accent:#9687ff}
        @media(max-width:1500px){.scai-launch-grid{grid-template-columns:repeat(6,minmax(0,1fr))}}@media(max-width:1220px){.scai-launch-grid{grid-template-columns:repeat(5,minmax(0,1fr))}}@media(max-width:980px){.scai-launch-grid{grid-template-columns:repeat(4,minmax(0,1fr))}}@media(max-width:720px){.scai-launch-grid{grid-template-columns:repeat(3,minmax(0,1fr))}.scai-launch-header{align-items:flex-start;flex-direction:column}.scai-launch-tools{flex:0 0 auto;width:100%}.scai-launchpad .scai-launch-body{padding:12px!important}}
        @media(max-width:460px){.scai-launch-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:5px}.scai-launch-icon{width:72px;height:72px;flex-basis:72px;border-radius:19px}.scai-launch-icon i{font-size:41px!important}.scai-launch-link{min-height:130px;padding:11px 5px 9px}.scai-launch-label{font-size:11.5px}.scai-launch-heading h1{font-size:19px!important}.scai-launch-heading-icon{width:40px;height:40px;flex-basis:40px}.scai-launch-sort select{max-width:85px}}
        @media print{.scai-launch-header,.scai-launch-filters,.scai-launch-star{display:none!important}.scai-launch-tile{break-inside:avoid}}
        @media(prefers-reduced-motion:reduce){.scai-launch-tile{transition:none}}
      `}</style>
    </div>
  );
}
